import { SWRResponse } from "swr";
import { ApiSchemaUser } from "../../models/api";
import { AxiosAuth } from "../axios";
import {
  useExtendedApiAccess,
  CreateAccessObject,
} from "./useExtendedApiAccess";
import { AppRouteApi } from "../../config/appRoute";
import { PaginatedData } from "../../models/api";
import { useRef, useEffect } from "react";
import { ApiResponseContainer } from "../../models/api";
import { useAppDispatch } from "../redux/ReduxStore";
import { ActionsUsersTable } from "../redux/slices/sliceUsersTable";
import { showErrorToast } from "../../components/toast.component";
import { ApiSchemaSuperUserProfile } from "../../models/api/superAdminProfile";

const CacheIds = {
  GetAll: AppRouteApi.Users.All(),
  ById: AppRouteApi.Users.ById(""),
  Profile: AppRouteApi.Users.Profile(),
};

function useApiUsersGetAll(): SWRResponse<ApiSchemaUser[] | undefined, Error> {
  const dispatch = useAppDispatch();
  const isMounted = useRef(false);

  const api = useExtendedApiAccess<ApiSchemaUser[]>(CacheIds.GetAll, () =>
    AxiosAuth.get(CacheIds.GetAll)
  );

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
    } else {
      (async () => {
        dispatch(ActionsUsersTable.setIsLoading(true));
        const res = await AxiosAuth.get<ApiResponseContainer<ApiSchemaUser[]>>(
          CacheIds.GetAll
        );

        if (res.data.isSuccess) {
          await api.mutate(res.data.data, false);
        }
        dispatch(ActionsUsersTable.setIsLoading(false));
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return api;
}

function useApiUsersById(
  id: string
): SWRResponse<ApiSchemaUser | undefined, Error> {
  return useExtendedApiAccess<ApiSchemaUser>(CacheIds.ById, () =>
    AxiosAuth.get(AppRouteApi.Users.ById(id))
  );
}

function useApiUsersProfile(): SWRResponse<
  ApiSchemaSuperUserProfile | undefined,
  Error
> {
  return useExtendedApiAccess<ApiSchemaSuperUserProfile>(CacheIds.Profile, () =>
    AxiosAuth.get(CacheIds.Profile)
  );
}

const AccessObject = {
  GetAll: CreateAccessObject<
    PaginatedData<ApiSchemaUser>,
    typeof useApiUsersGetAll
  >(CacheIds.GetAll, useApiUsersGetAll),

  ById: CreateAccessObject<ApiSchemaUser, typeof useApiUsersById>(
    CacheIds.ById,
    useApiUsersById
  ),

  Profile: CreateAccessObject<
    ApiSchemaSuperUserProfile,
    typeof useApiUsersProfile
  >(CacheIds.Profile, useApiUsersProfile),
};

async function AddUsers(
  user: any
): Promise<ApiResponseContainer<ApiSchemaUser>> {
  try {
    const response = await AxiosAuth.post<ApiResponseContainer<ApiSchemaUser>>(
      AppRouteApi.Users.Root(),
      user
    );
    if (response.data.isSuccess) {
      const updAction = (
        res?: PaginatedData<ApiSchemaUser>
      ): PaginatedData<ApiSchemaUser> | undefined => {
        if (res && Array.isArray(res?.list) && response.data.data) {
          return {
            totalCount: res.totalCount + 1,
            list: [...res.list, response.data.data],
          };
        } else if (res) return res;
      };
      AccessObject.GetAll.Mutate(updAction());
      return response.data;
    }
  } catch (e: any) {
    return showErrorToast({ isSuccess: false, ...JSON.parse(e.message).data });
  }
  return { isSuccess: false };
}

async function UpdateUsers(
  user: any
): Promise<ApiResponseContainer<ApiSchemaUser>> {
  try {
    const response = await AxiosAuth.put<ApiResponseContainer<ApiSchemaUser>>(
      AppRouteApi.Users.Root(),
      user
    );
    if (response.data.isSuccess) {
      const updAction = (
        res?: PaginatedData<ApiSchemaUser>
      ): PaginatedData<ApiSchemaUser> | undefined => {
        if (res && Array.isArray(res?.list) && response.data.data) {
          return {
            totalCount: res.totalCount + 1,
            list: [...res.list, response.data.data],
          };
        } else if (res) return res;
      };
      AccessObject.GetAll.Mutate(updAction());
      return response.data;
    }
  } catch (e: any) {
    return showErrorToast({ isSuccess: false, ...JSON.parse(e.message).data });
  }
  return { isSuccess: false };
}

async function DeleteUsers(ids: string[]): Promise<boolean> {
  try {
    const response = await AxiosAuth.delete<ApiResponseContainer<boolean>>(
      AppRouteApi.Users.Root(),
      {
        data: ids,
      }
    );
    if (response.data.isSuccess) return true;
  } catch (e: any) {
    showErrorToast({ isSuccess: false, ...JSON.parse(e.message).data });
  }
  return false;
}

async function ChangeAccountSettings(
  data: FormData
): Promise<ApiResponseContainer<Partial<ApiSchemaUser>>> {
  try {
    const response = await AxiosAuth.put<
      ApiResponseContainer<Partial<ApiSchemaUser>>
    >(AppRouteApi.Users.Settings(), data);
    if (response.data.isSuccess) {
      return response.data;
    }
  } catch (e: any) {
    return showErrorToast({ isSuccess: false, ...JSON.parse(e.message).data });
  }
  return { isSuccess: false };
}

export const ApiUsers = {
  ById: AccessObject.ById,
  Profile: AccessObject.Profile,
  GetAll: AccessObject.GetAll,
  Add: AddUsers,
  Delete: DeleteUsers,
  Update: UpdateUsers,
  ChangeAccountSettings,
};
